import {Table, TableReorderLocks} from 'notebook/tables/constants/TableConstants';
import {SelectionRange, TableDimension, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import TableUtil from 'notebook/tables/TableUtil';
import APIUtil from 'util/APIUtil';

interface ShouldShowDragHandlesParams {
  locked: boolean;
  fileId: string;
  isProtected: boolean;
  allowReorderDimensions: TableReorderLocks | undefined;
  table: Table;
  canModifyRowDim: boolean;
  canModifyColumnDim: boolean;
}

/**
 * Whether the drag handle for reordering the given dimension (row or column) should be shown.
 */
export function computeShouldShowDragHandles(
  dimension: TableDimension,
  {
    locked,
    fileId,
    isProtected,
    allowReorderDimensions,
    table,
    canModifyRowDim,
    canModifyColumnDim,
  }: ShouldShowDragHandlesParams
): boolean {
  if (
    locked ||
    APIUtil.isStageEntry(fileId) ||
    APIUtil.isStageEntryTemplate(fileId) ||
    // Disable dragging columns for fill-in only entries, but allow dragging rows
    (isProtected && dimension === TableDimension.COLUMN)
  ) {
    return false;
  }

  let allowReorderDimension: boolean;
  if (allowReorderDimensions) {
    allowReorderDimension = allowReorderDimensions[dimension];
  } else if (TableUtil.isStructuredTable(table._tableType)) {
    allowReorderDimension = false;
  } else if (dimension === TableDimension.ROW) {
    allowReorderDimension = canModifyRowDim;
  } else {
    allowReorderDimension = canModifyColumnDim;
  }

  if (dimension === TableDimension.ROW) {
    return table.table.length > 1 && allowReorderDimension;
  }
  return table.table[0].length > 1 && allowReorderDimension;
}

/**
 * The dimension and range currently being dragged for reordering. Both are null when nothing is
 * being dragged (or the table is locked / has no active multi-selection).
 */
export function computeTableDraggingState(
  locked: boolean,
  draggingDimension: TableDimension | null,
  multiStartIndex: TableIndex | null,
  multiEndIndex: TableIndex | null
): {dimension: TableDimension | null; range: SelectionRange | null} {
  if (locked || !draggingDimension || multiStartIndex == null || multiEndIndex == null) {
    return {dimension: null, range: null};
  }
  const dimIdx = draggingDimension === TableDimension.ROW ? 0 : 1;
  const [startIndex, endIndex] = [multiStartIndex[dimIdx], multiEndIndex[dimIdx]].sort((a, b) => a - b);
  return {
    dimension: draggingDimension,
    range: {minIndex: startIndex, maxIndex: endIndex},
  };
}
